import React, { createContext, useContext, useState, useEffect, PropsWithChildren } from 'react';
import axios from 'axios';
import { useIdentity } from '@/hooks/useIdentity';
import { useAppMode } from '@/contexts/AppModeContext';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';

/**
 * Subscription Information
 */
export interface Subscription {
  subscriptionId: string;
  internalReferenceId: string;
  userId: string;
  createdAt: string;
  organizationName: string;
}

interface SubscriptionContextValue {
  subscriptionId: string | null;
  platformUserId: string | null;
  subscriptions: Subscription[];
  loading: boolean;
  error: string | null;
  selectSubscription: (id: string) => void;
  refetch: () => Promise<void>;
  showSubscriptionPicker: boolean;
  setShowSubscriptionPicker: (show: boolean) => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

/**
 * Subscription Provider
 * 
 * Manages subscription for all modes:
 * 
 * NATIVE TAB MODE:
 * - Receives instanceId from ProductProvider
 * - Uses instanceId directly as subscriptionId
 * - No API call needed
 * 
 * STANDALONE MODE:
 * - Fetches all subscriptions user has access to (by email)
 * - Auto-selects the oldest subscription
 * - Allows user to switch subscriptions if multiple exist
 * 
 * E2B MODE:
 * - No subscription header is sent
 * - Backend handles requests without multi-tenancy
 * 
 * The selected subscriptionId is used in the X-Subscription-Id header
 * for all API requests (except E2B mode).
 */
export function SubscriptionProvider({ children, instanceId }: PropsWithChildren<{ instanceId?: string }>) {
  const { isStandalone, isNativeTab, isE2B } = useAppMode();
  const { isAuthenticated, getAccessToken } = useIdentity();
  const backendUrl = useGetBackendUrl();

  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  // Start with loading=true for standalone mode to prevent flash of "No Subscription" error
  // The fetch will set this to false when complete
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubscriptionPicker, setShowSubscriptionPicker] = useState(false);

  /**
   * Handle non-standalone modes:
   * - Native Tab: use instanceId directly from ProductProvider
   * - E2B: no subscription header needed
   */
  useEffect(() => {
    if (isNativeTab && instanceId) {
      setSubscriptionId(instanceId);
      setLoading(false);
    } else if (isE2B) {
      // E2B mode doesn't need subscription
      setLoading(false);
    } else if (isNativeTab && !instanceId) {
      // Native tab but waiting for instanceId
      setLoading(true);
    }
  }, [isNativeTab, isE2B, instanceId]);

  /**
   * Fetch subscriptions from backend
   */
  const fetchSubscriptions = async () => {
    if (!isStandalone || !isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      
      const token = await getAccessToken();
      const response = await axios.get<Subscription[]>(`${backendUrl}/api/subscriptions/my-subscriptions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSubscriptions(response.data);
      console.log('[SubscriptionContext] Fetched subscriptions:', response.data.length);

      if (response.data.length === 0) {
        console.warn('[SubscriptionContext] No subscriptions found for user');
        setError('No subscriptions found. Please contact support.');
      } else if (response.data.length === 1) {
        // Auto-select if only one subscription
        setSubscriptionId(response.data[0].subscriptionId);
      } else {
        // Multiple subscriptions - show picker modal
        console.log('[SubscriptionContext] Multiple subscriptions found, showing picker');
        setShowSubscriptionPicker(true);
      }
    } catch (err: any) {
      console.error('[SubscriptionContext] Error fetching subscriptions:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch subscriptions when user is authenticated in standalone mode
   * Keep loading=true while waiting for auth, then fetch
   */
  useEffect(() => {
    if (isStandalone) {
      if (isAuthenticated) {
        // Authenticated - fetch subscriptions
        fetchSubscriptions();
      }
      // If not authenticated yet, loading stays true (handled by useIdentity hook)
    }
  }, [isStandalone, isAuthenticated]);

  /**
   * Manual subscription selection
   */
  const selectSubscription = (id: string) => {
    setSubscriptionId(id);
  };

  const [platformUserId, setPlatformUserId] = useState<string | null>(null);

  // Fetch platform userId for all authenticated modes
  useEffect(() => {
    if (!isAuthenticated || platformUserId) return;

    // If we already have it from the subscriptions fetch (standalone mode)
    const fromSubscriptions = subscriptions.find(s => s.subscriptionId === subscriptionId)?.userId;
    if (fromSubscriptions) {
      setPlatformUserId(fromSubscriptions);
      return;
    }

    // For native tab / other modes: fetch from API
    if (!subscriptionId) return;

    const fetchUserId = async () => {
      try {
        const token = await getAccessToken();
        const response = await axios.get<Subscription[]>(`${backendUrl}/api/subscriptions/my-subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const match = response.data.find(s => s.subscriptionId === subscriptionId);
        if (match?.userId) {
          setPlatformUserId(match.userId);
        }
      } catch (err) {
        console.error('[SubscriptionContext] Failed to fetch platform userId:', err);
      }
    };

    fetchUserId();
  }, [isAuthenticated, subscriptionId, subscriptions]);

  const value: SubscriptionContextValue = {
    subscriptionId,
    platformUserId,
    subscriptions,
    loading,
    error,
    selectSubscription,
    refetch: fetchSubscriptions,
    showSubscriptionPicker,
    setShowSubscriptionPicker,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Hook to access subscription context
 * 
 * Use this hook in Standalone mode to get the current subscription ID
 * and add it to API request headers.
 * 
 * @example
 * const { subscriptionId } = useSubscription();
 * 
 * axios.get('/api/workflows', {
 *   headers: { 'X-Subscription-Id': subscriptionId }
 * });
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);
  
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  
  return context;
}
